import React, { useState, useEffect, useCallback } from "react";
import ActionDropdown from "../common/ui/actiondropdown";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import AddSection from "./addsection";
import Sectionheading from "./sectionheading";
import { useLoader } from "../../context/loadercontext";
import toast from "react-hot-toast";
import { DeletePreviewModal, FinalDeleteConfirmModal } from "../common/ui/deletemodal";
import { useAuth } from "../../context/AuthContext";
import NoFound from "../common/ui/NoFound";
import WarningModal from "../common/ui/warningmodel";

export default function Viewsection() {
  const [sections, setSections] = useState([]);
  const { setLoading } = useLoader();

  const [openRowIndex, setOpenRowIndex] = useState(null);
  const [showDeletePreview, setShowDeletePreview] = useState(false); // 🆕 first modal
  const [showFinalDeleteConfirm, setShowFinalDeleteConfirm] = useState(false); // 🆕 second modal
  const [deleteIndex, setDeleteIndex] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [warningVisible, setWarningVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  const { logout } = useAuth();

  const fetchSections = useCallback(async () => {
    setLoading(true);
    const startTime = Date.now();
    try {
      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SECTIONS.GET_ALL}`,
        { method: "GET", headers: getHeaders(true) }
      );

      // -------------------------
      // 🔥 If ACCESS TOKEN expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with new token
          response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SECTIONS.GET_ALL}`,
            { method: "GET", headers: getHeaders(true) }
          );
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }

      const result = await response.json();

      if (result.status === 200) {
        const mappedData = result.data.map((s) => ({
          id: s.id,
          sectionName: s.section_name,
          sectionDescription: s.section_desc,
          createdAt: s.created_at,
        }));
        setSections(mappedData);
        const elapsed = Date.now() - startTime;
        const remainingDelay = Math.max(0, 1000 - elapsed);
        setTimeout(() => setLoading(false), remainingDelay);
      } else {
        console.error("Failed to fetch sections:", result.message);
        setLoading(true);
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
      setLoading(true);
    }
  }, [setLoading, logout]);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  const handleRowToggle = (index) =>
    setOpenRowIndex(openRowIndex === index ? null : index);

  const handleAddClick = () => setShowAddModal(true);

  const handleEdit = (index) => {
    setEditData(sections[index]);
    setShowEditModal(true);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditData(null);
  };

  // 🆕 Step 1: Open preview modal before delete confirmation
  const handleDeleteClick = (index) => {
    setDeleteIndex(index);
    setShowDeletePreview(true);
  };

  // 🆕 Step 2: After user confirms preview, show final confirmation modal
  const handleProceedToFinalConfirm = () => {
    setShowDeletePreview(false);
    setShowFinalDeleteConfirm(true);
  };

  const handleCancelPreview = () => {
    setShowDeletePreview(false);
    setDeleteIndex(null);
  };

  // 🧾 Final confirmation — perform delete API
  const handleConfirmDelete = async () => {
    try {
      const sectionId = sections[deleteIndex]?.id;
      if (!sectionId) return;
      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SECTIONS.SOFT_DELETE(sectionId)}`,
        { method: "PUT", headers: getHeaders(true) }
      );

      // -------------------------
      // 🔥 If ACCESS TOKEN expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with new token
          response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SECTIONS.SOFT_DELETE(sectionId)}`,
            { method: "PUT", headers: getHeaders(true) }
          );
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }



      const result = await response.json();

      if (response.ok && (result.status === 200 || result.message?.includes("deleted"))) {
        toast.success("Section deleted successfully");
        await fetchSections();
      } else {
        setWarningMessage(result.message || "Failed to delete section");
        setWarningVisible(true);
        console.error("Failed to delete section:", result);
      }
    } catch (error) {
      console.error("Error deleting section:", error);
      setWarningMessage("Error deleting section");
      setWarningVisible(true);
    } finally {
      setShowFinalDeleteConfirm(false);
      setDeleteIndex(null);
    }
  };

  //Format Date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  return (
    <>
      <Sectionheading onAddClick={handleAddClick} />

      <div className="mt-6 overflow-x-auto">
        <table className="min-w-[950px] w-full text-table text-center">
          <thead className="border-b border-gray-200 text-gray-600 font-medium">
            <tr>
              <th className="px-4 py-3">Sr No.</th>
              <th className="px-4 py-3">Section Name</th>
              <th className="px-4 py-3">Section Description</th>
              <th className="px-4 py-3">Created At</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {sections.length > 0 ? (
              sections.map((row, idx) => (
                <tr
                  key={row.id || idx}
                  className="border-b hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleRowToggle(idx)}
                >
                  <td className="px-3 py-2">{idx + 1}</td>
                  <td className="px-3 py-2">{row.sectionName ? row.sectionName.charAt(0).toUpperCase() + row.sectionName.slice(1) : ""}</td>
                  <td className="px-3 py-2">
                    {row.sectionDescription
                      ? row.sectionDescription
                        .toLowerCase()
                        .split(",") // split by comma only
                        .map(part =>
                          part
                            .trim()
                            .split(" ")
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(" ")
                        )
                        .join(", ") // add comma + space back
                      : ""}
                  </td>
                  <td className="px-3 py-1">{formatDate(row.createdAt)}</td>
                  <td
                    className="px-3 py-1 text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ActionDropdown
                      actions={{ edit: true, delete: true }}
                      onEdit={() => handleEdit(idx)}
                      onDelete={() => handleDeleteClick(idx)}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="py-4 text-gray-500">
                  <NoFound />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AddSection
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSectionAdded={fetchSections}
      />

      <AddSection
        isOpen={showEditModal}
        onClose={handleCloseEdit}
        editData={editData}
        onSectionAdded={fetchSections}
      />

      {/* 🧩 Reusable Modals */}
      {showDeletePreview && deleteIndex !== null && (
        <DeletePreviewModal
          title="Section Details"
          data={{
            Name: sections[deleteIndex]?.sectionName ? sections[deleteIndex].sectionName.toLowerCase().split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ") : "",
            Description: sections[deleteIndex]?.sectionDescription
              ? sections[deleteIndex].sectionDescription
                .toLowerCase()
                .split(",") // split by comma only
                .map(part =>
                  part
                    .trim()
                    .split(" ")
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")
                )
                .join(", ") // add comma + space back
              : "",
            "Created At": formatDate(sections[deleteIndex]?.createdAt),
          }}
          onCancel={handleCancelPreview}
          onProceed={handleProceedToFinalConfirm}
        />
      )}

      {showFinalDeleteConfirm && (
        <FinalDeleteConfirmModal
          title="Confirm Delete"
          message="Are you sure you want to permanently delete this section?"
          onCancel={() => setShowFinalDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
      {warningVisible && (
        <WarningModal
          message={warningMessage}
          onClose={() => setWarningVisible(false)}
        />
      )}

    </>
  );
}
